import { ref } from "vue";
import { useAuthenticatedFetch } from "../auth/useAuthenticatedFetch";
import {
  SubscriptionQueryParams,
  PausedSubscriptionMemberRequest,
  CancelledSubscriptionMemberRequest,
  updateSubscriptionDateFinRequest,
  updateContractStatusRequest,
} from "@/interfaces/subscriptionMember";
import {
  SubscriptionSalesQueryParams,
  PackCountSalesQueryParams,
} from "@/interfaces/stats";
import {
  subscriptions_members,
  clubUUID,
  paginationLinks,
  summary_subscriptions,
  details_subscription,
  subscriptions_amount_sales,
  subscriptions_amount_sales_by_day,
  packs_count_sales,
  last_subscription_member,
  revenu_overview,
  currentClub,
} from "@/shared/state";
import Swal from "sweetalert2";
import i18n from "@/i18n/index";

import { useGlobal } from "@/composables/global/useGlobal";

const { authenticatedFetch } = useAuthenticatedFetch();
const API_URL = process.env.VUE_APP_ROOT_API;

export function useSubscription() {
  const { formatDate } = useGlobal();
  const error = ref<string | null>(null);
  const errorMemberSubscription = ref<boolean | false>(false);
  const isRequestSubscription = ref<boolean | false>(false);
  const Toast = Swal.mixin({
    toast: true,
    position: "top-right",
    showConfirmButton: false,
    timer: 12000,
    timerProgressBar: true,
  });

  const fetchSubscriptionMembers = async (body: SubscriptionQueryParams) => {
    try {
      isRequestSubscription.value = true;
      subscriptions_members.data = [];
      paginationLinks.value = 0;
      const response = await authenticatedFetch(
        `${API_URL}members/subscriptions/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );
      if (response) {
        if (body?.type === "paginate") {
          summary_subscriptions.data = response?.summary[0] || {};
          subscriptions_members.data = response?.subscriptions?.data || [];
          paginationLinks.value = response?.subscriptions || [];
          delete paginationLinks.value.data;
        } else if (body?.type === "all") {
          if (response?.summary) {
            summary_subscriptions.data = response?.summary || {};
            subscriptions_members.data = response?.subscriptions || {};
          } else {
            subscriptions_members.data = response || {};
          }
        } else {
          summary_subscriptions.data = response?.summary || {};
          details_subscription.data = response?.subscriptions || {};
        }
        isRequestSubscription.value = false;
      }
    } catch (err: any) {
      error.value = err.message;
      isRequestSubscription.value = false;
    }
  };

  const fetchCreditExportFile = async (body: SubscriptionQueryParams) => {
    try {
      isRequestSubscription.value = true;
      const response = await authenticatedFetch(
        `${API_URL}members/subscriptions/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );

      if (response) {
        isRequestSubscription.value = false;
        return {
          summary_subscriptions: response?.summary[0] || {},
          subscriptions_members: response?.subscriptions || {},
        };
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };

  const fetchGlobalRevenu = async (body: SubscriptionSalesQueryParams) => {
    try {
      isRequestSubscription.value = true;
      subscriptions_amount_sales.data = [];
      const response = await authenticatedFetch(
        `${API_URL}revenu/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );
      if (response) {
        isRequestSubscription.value = false;
        subscriptions_amount_sales.data = response?.subscriptions;
        subscriptions_amount_sales.total = response?.total;
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };
  const fetchGlobalRevenuByDay = async (body: SubscriptionSalesQueryParams) => {
    try {
      isRequestSubscription.value = true;
      subscriptions_amount_sales_by_day.data = [];
      const response = await authenticatedFetch(
        `${API_URL}revenu/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );
      if (response) {
        isRequestSubscription.value = false;
        subscriptions_amount_sales_by_day.data = response?.subscriptions;
        subscriptions_amount_sales_by_day.total = response?.total;
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };

  const fetchNumberPackSalesStats = async (body: PackCountSalesQueryParams) => {
    try {
      isRequestSubscription.value = true;
      packs_count_sales.data = [];
      const response = await authenticatedFetch(
        `${API_URL}members/packs/search/sales`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );
      if (response) {
        isRequestSubscription.value = false;
        packs_count_sales.data = response?.packs;
        packs_count_sales.total_count = response?.total_count;
        packs_count_sales.total_amount = response?.total_amount;
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };

  const getLastSubscriptionMember = async (memberUUID: string) => {
    try {
      isRequestSubscription.value = true;
      const response = await authenticatedFetch(
        `${API_URL}member/subscription/last/${memberUUID}/${clubUUID.value}`,
        {
          method: "GET",
        },
        false,
        "",
        false,
      );
      isRequestSubscription.value = false;
      last_subscription_member.data = response?.data || [];
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };

  // const editMember = async (body: MemberRequest) => {
  //     try {
  //         const response = await authenticatedFetch(`${API_URL}members/edit/${body?.id}/${clubUUID.value}`, {
  //             method: 'PUT',
  //             body: JSON.stringify(body),
  //         }, true);
  //         if (response) {
  //             const memberEdited: Member = response?.data
  //             const index = members?.data?.findIndex(member => member?.id === memberEdited?.id);
  //             if (index !== -1) {
  //                 members.data[index] = memberEdited;
  //             }
  //         }
  //     } catch (err: any) {
  //         error.value = err.message;
  //     }
  // };

  const paymentReminder = async (
    memberUUID: string,
    id: number,
    channel: string,
  ) => {
    Swal.fire({
      title: i18n.t("message.confirmation.paymentremider.title"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: i18n.t("button.send"),
      cancelButtonText: i18n.t("button.cancel"),
      allowOutsideClick: false, // ✅ disable closing by clicking outside
      allowEscapeKey: false, // (optional) disable closing with ESC
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          isRequestSubscription.value = true;
          const response = await authenticatedFetch(
            `${API_URL}member/payment-reminder/${memberUUID}/${clubUUID.value}/${id}/${channel}`,
            {
              method: "GET",
            },
            false,
            "",
            false,
          );
          if (response?.data || response?.message) {
            Toast.fire({
              title: i18n.t("message.toast.paymentremider.success"),
              icon: "success",
            });
          } else {
            Toast.fire({
              title: i18n.t("message.toast.paymentremider.error"),
              icon: "error",
            });
          }
          isRequestSubscription.value = false;
        } catch (err: any) {
          error.value = err.message;
          isRequestSubscription.value = false;
        }
      }
    });
  };

  const deleteSubscriptionMember = async (id: number) => {
    Swal.fire({
      title: i18n.t("message.confirmation.subscription.delete.title"),
      text: i18n.t("message.confirmation.subscription.delete.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: i18n.t("button.delete"),
      cancelButtonText: i18n.t("button.cancel"),
      allowOutsideClick: false, // ✅ disable closing by clicking outside
      allowEscapeKey: false, // (optional) disable closing with ESC
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          isRequestSubscription.value = true;
          const response = await authenticatedFetch(
            `${API_URL}member/subscriptions/delete/${id}/${clubUUID.value}`,
            {
              method: "DELETE",
            },
            true,
            "",
            true,
          );
          const index = subscriptions_members?.data?.findIndex(
            (subscription_member) => subscription_member?.id === id,
          );
          if (index !== -1) {
            subscriptions_members.data.splice(index, 1);
            isRequestSubscription.value = false;
          }
        } catch (err: any) {
          isRequestSubscription.value = false;
          error.value = err.message;
        }
      }
    });
  };
  const deleteSubscriptionFromFiche = async (id: number) => {
    try {
      isRequestSubscription.value = true;
      const response = await authenticatedFetch(
        `${API_URL}member/subscriptions/delete/${id}/${clubUUID.value}`,
        {
          method: "DELETE",
        },
        true,
        "",
        true,
      );
      isRequestSubscription.value = false;
      return response;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
      return err;
    }
  };

  const getRevenuOverview = async () => {
    try {
      isRequestSubscription.value = true;
      const response = await authenticatedFetch(
        `${API_URL}revenu/overview/${clubUUID.value}`,
        {
          method: "GET",
        },
        false,
        "",
        false,
      );

      if (response) {
        isRequestSubscription.value = false;
        revenu_overview.today = formatDate(response?.data?.today);
        revenu_overview.month = response?.data?.month;
        revenu_overview.year = response?.data?.year;
        revenu_overview.value.today_total =
          response?.data?.value?.today_total ?? 0;
        revenu_overview.value.month_total =
          response?.data?.value?.month_total ?? 0;
        revenu_overview.value.year_total =
          response?.data?.value?.year_total ?? 0;
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      error.value = err.message;
    }
  };

  const cancelMemberSubscription = async (
    body: CancelledSubscriptionMemberRequest,
  ) => {
    try {
      isRequestSubscription.value = true;
      errorMemberSubscription.value = false;
      const customToastMessage = i18n.t(
        "message.toast.subscription_canceled.title",
      );
      const response = await authenticatedFetch(
        `${API_URL}member/subscription/cancel/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        customToastMessage,
        true,
      );
      if (response) {
        isRequestSubscription.value = false;
        return response;
      }
      isRequestSubscription.value = false;
    } catch (err: any) {
      isRequestSubscription.value = false;
      errorMemberSubscription.value = true;
    }
  };

  const memberPauseSubscription = async (
    body: PausedSubscriptionMemberRequest,
  ) => {
    try {
      isRequestSubscription.value = true;
      errorMemberSubscription.value = false;
      const customToastMessage =
        body?.type == "pause"
          ? i18n.t("message.toast.subscription_paused.title")
          : i18n.t("message.toast.subscription_resumed.title");
      const response = await authenticatedFetch(
        `${API_URL}member/subscription/pause/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        customToastMessage,
        true,
      );
      if (response) {
        isRequestSubscription.value = false;
        return response;
      }
    } catch (err: any) {
      isRequestSubscription.value = false;
      errorMemberSubscription.value = true;
    }
  };
  const updateSubscriptionEndDate = async (
    body: updateSubscriptionDateFinRequest,
  ) => {
    try {
      isRequestSubscription.value = true;
      errorMemberSubscription.value = false;
      const customToastMessage = i18n.t(
        "message.toast.subscription_paused.title",
      );
      const response = await authenticatedFetch(
        `${API_URL}member/subscription/edit/datefin/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        customToastMessage,
        true,
      );
      if (response) {
        isRequestSubscription.value = false;
        return response;
      }
    } catch (err: any) {
      isRequestSubscription.value = false;
      errorMemberSubscription.value = true;
    }
  };
  const updateContractStatus = async (body: updateContractStatusRequest) => {
    try {
      isRequestSubscription.value = true;
      errorMemberSubscription.value = false;
      const customToastMessage = i18n.t(
        "message.toast.subscription_paused.title",
      );
      const response = await authenticatedFetch(
        `${API_URL}member/subscriptions/contract-status/${body?.id}/${clubUUID.value}`,
        {
          method: "post",
          body: JSON.stringify(body),
        },
        true,
        customToastMessage,
        true,
      );
      if (response) {
        isRequestSubscription.value = false;
        return response;
      }
    } catch (err: any) {
      isRequestSubscription.value = false;
      errorMemberSubscription.value = true;
    }
  };
  return {
    getRevenuOverview,
    paymentReminder,
    fetchSubscriptionMembers,
    fetchGlobalRevenu,
    fetchGlobalRevenuByDay,
    fetchNumberPackSalesStats,
    getLastSubscriptionMember,
    cancelMemberSubscription,
    memberPauseSubscription,
    fetchCreditExportFile,
    isRequestSubscription,
    deleteSubscriptionMember,
    deleteSubscriptionFromFiche,
    updateSubscriptionEndDate,
    updateContractStatus,
  };
}
